import { Provider } from "risa-data-model";
import { AuthStatusOptionModel } from "../../../data-model/authStatusOptions";
import { BOOptionModel } from "../../../data-model/boOptionModel";
import { FilterSection } from "../../../data-model/filterValues";
import { PostgresFacetCountResponseModel } from "../../../data-model/postgresFacetCountResponseModel";
import { FilterCreationConfig } from "./filterCreationConfig";

/** Parameters required to build the complete set of filter sections. */
interface FilterDataBuilderParams {
  postgresFacetResults: PostgresFacetCountResponseModel;
  authStatusOptions: AuthStatusOptionModel[];
  /** BO status tiers for the `boStatus` filter section. */
  boOptions: BOOptionModel[];
  /** Estimate tiers for the `estimateValue` filter section (exception-flow orgs). */
  estimateOptions: BOOptionModel[];
  allProvidersDetails: Provider[];
  filterCreationConfig: FilterCreationConfig[];
}

/**
 * Sorts a filter section's options alphabetically (case-insensitive) by label.
 * Date-type sections carry no selectable options, so they are returned untouched.
 *
 * @param section - A built FilterSection
 * @returns The same section with its options sorted alphabetically by label
 */
const sortFilterSectionOptions = (section: FilterSection): FilterSection => {
  if (section.type === "date" || !Array.isArray(section.options)) {
    return section;
  }

  return {
    ...section,
    options: [...section.options].sort((a, b) =>
      a.label.localeCompare(b.label),
    ),
  };
};

/**
 * Builds the complete filter data array from various data sources
 * using a configuration-driven approach for better maintainability.
 * Every section's options are sorted alphabetically by label in one
 * central place so individual generators don't each need to sort.
 *
 * @param params - Destructured FilterDataBuilderParams containing facet results,
 *   auth status options, BO/estimate options, provider details, and filter config
 * @returns Array of FilterSection objects ready for the filter panel UI
 */
export const buildFilterData = ({
  postgresFacetResults,
  authStatusOptions,
  boOptions,
  estimateOptions,
  allProvidersDetails,
  filterCreationConfig,
}: FilterDataBuilderParams): FilterSection[] => {
  const dataMap = {
    boStatus: boOptions ?? [],
    estimateValue: estimateOptions ?? [],
    authStatus: authStatusOptions ?? [],
    assignee: allProvidersDetails ?? [],
  };

  return filterCreationConfig.map((config) => {
    if (!config.requiresData) {
      return sortFilterSectionOptions(config.createFunction());
    }

    let data: any;
    if (config.dataExtractor) {
      data = config.dataExtractor(postgresFacetResults);
    } else if (dataMap[config.id as keyof typeof dataMap]) {
      data = dataMap[config.id as keyof typeof dataMap];
    }

    return sortFilterSectionOptions(config.createFunction(data));
  });
};

/**
 * Checks if all required data dependencies are loaded and available
 * before attempting to build the filter sections.
 *
 * BO and estimate option arrays are required only when the active
 * `filterCreationConfig` includes a matching section (`boStatus` or
 * `estimateValue`), so orgs that use one filter type are not blocked
 * waiting for the other option list.
 *
 * @param params - Object containing Postgres facet results, auth/BO/estimate options,
 *   provider details, and filter creation config
 * @returns True when all dependencies are present and non-empty
 */
export const areFilterDependenciesReady = ({
  postgresFacetResults,
  authStatusOptions,
  boOptions,
  estimateOptions,
  providerDetails,
  filterCreationConfig,
}: Omit<FilterDataBuilderParams, "allProvidersDetails"> & {
  providerDetails: any;
  postgresFacetResults: PostgresFacetCountResponseModel;
}): boolean => {
  const requiresBoStatus = filterCreationConfig.some(
    (config) => config.id === "boStatus",
  );
  const requiresEstimateValue = filterCreationConfig.some(
    (config) => config.id === "estimateValue",
  );

  return !!(
    postgresFacetResults &&
    authStatusOptions &&
    authStatusOptions.length > 0 &&
    (!requiresBoStatus || (boOptions && boOptions.length > 0)) &&
    (!requiresEstimateValue ||
      (estimateOptions && estimateOptions.length > 0)) &&
    providerDetails &&
    filterCreationConfig &&
    filterCreationConfig.length > 0
  );
};
